import { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Alert, alpha, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { COLORS } from '../../theme/themeConstants';
import { useFeaturedRestaurantApi } from '../../Hooks/useFeaturedRestaurantApi';
import { hasHashInUrl, getUrlInfo } from '../../utils/hashUrlUtils';
import { useTranslation } from 'react-i18next';

const PaymentFeaturedHyperPaySuccess = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyFeaturedPayment, loading } = useFeaturedRestaurantApi();
  const [resultMessage, setResultMessage] = useState<string>('');
  const [success, setSuccess] = useState<boolean | null>(null);
  const hasVerified = useRef(false);

  // Check if hash is present in URL and log URL information
  const urlHasHash = hasHashInUrl();
  const urlDebugInfo = getUrlInfo();
  
  console.log('=== Payment Success Page ===');
  console.log('Has hash (#) present?', urlHasHash);
  console.log('Full URL info:', urlDebugInfo);
  console.log('Search params:', Object.fromEntries(searchParams.entries()));
  
  // Debug localStorage
  console.log('LocalStorage data:', {
    checkoutId: localStorage.getItem('hyperpay_checkout_id'),
    merchantId: localStorage.getItem('hyperpay_merchant_id'),
    subscriptionId: localStorage.getItem('hyperpay_subscription_id')
  });

  const subscriptionId = useMemo(() => {
    const fromURL = searchParams.get('subscriptionId');
    const fromStorage = localStorage.getItem('hyperpay_subscription_id');
    console.log('SubscriptionId sources:', { fromURL, fromStorage });
    return fromURL || fromStorage || '';
  }, [searchParams]);
  
  const checkoutId = useMemo(() => {
    const fromURLId = searchParams.get('id');
    const fromURLCheckoutId = searchParams.get('checkoutId');
    const fromStorage = localStorage.getItem('hyperpay_checkout_id');
    console.log('CheckoutId sources:', { fromURLId, fromURLCheckoutId, fromStorage });
    return fromURLId || fromURLCheckoutId || fromStorage || '';
  }, [searchParams]);
  
  const merchantIdParam = useMemo(() => {
    const fromURL = searchParams.get('merchantId');
    const fromStorage = localStorage.getItem('hyperpay_merchant_id');
    console.log('MerchantId sources:', { fromURL, fromStorage });
    return fromURL || fromStorage || '';
  }, [searchParams]);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerified.current) return;
    
    // Get merchant ID from URL params, localStorage, or userData
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const merchantId: string = merchantIdParam || userData?.userId || '';
    
    console.log('Payment verification params:', { 
      merchantId, 
      checkoutId, 
      subscriptionId,
      fromURL: {
        merchantId: !!searchParams.get('merchantId'),
        checkoutId: !!(searchParams.get('id') || searchParams.get('checkoutId'))
      },
      fromLocalStorage: {
        merchantId: !!localStorage.getItem('hyperpay_merchant_id'),
        checkoutId: !!localStorage.getItem('hyperpay_checkout_id')
      }
    });
    
    if (!merchantId || !checkoutId) {
      console.error('Missing required params:', { merchantId: !!merchantId, checkoutId: !!checkoutId });
      console.error('All available data:', {
        urlParams: Object.fromEntries(searchParams.entries()),
        localStorage: {
          checkoutId: localStorage.getItem('hyperpay_checkout_id'),
          merchantId: localStorage.getItem('hyperpay_merchant_id'),
          subscriptionId: localStorage.getItem('hyperpay_subscription_id')
        },
        userData: JSON.parse(localStorage.getItem('userData') || '{}')
      });
      setSuccess(false);
      
      // Provide more specific error message
      if (!checkoutId && !merchantId) {
        setResultMessage('Payment information is missing. This usually happens when navigating directly to this page. Please start a new payment from the Featured Restaurant page.');
      } else if (!checkoutId) {
        setResultMessage('Checkout ID is missing. This might be a routing issue. Please check the browser console for debug information or contact support.');
      } else {
        setResultMessage('Merchant ID is missing. Please ensure you are logged in and try again.');
      }
      return;
    }

    // Mark as verified to prevent re-runs
    hasVerified.current = true;

    // Verify payment with backend
    verifyFeaturedPayment({ checkoutId: String(checkoutId), merchantId: String(merchantId) })
      .then((resp) => {
        setSuccess(Boolean(resp.success));
        setResultMessage(resp.message || 'Payment verified successfully! Your featured listing is now active.');
        
        // Clean up localStorage after successful verification
        try {
          localStorage.removeItem('hyperpay_checkout_id');
          localStorage.removeItem('hyperpay_merchant_id');
          localStorage.removeItem('hyperpay_subscription_id');
          console.log('Cleaned up payment data from localStorage');
        } catch (err) {
          console.error('Failed to clean up localStorage:', err);
        }
      })
      .catch((err) => {
        setSuccess(false);
        const errorMsg = err instanceof Error ? err.message : 'Payment verification failed. Please contact support if amount was deducted.';
        setResultMessage(errorMsg);
      });
  }, [checkoutId, merchantIdParam, subscriptionId, searchParams, verifyFeaturedPayment]);

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)'
    }}>
      <Paper sx={{
        p: 4,
        maxWidth: 560,
        width: '100%',
        border: '1px solid #e8eaed',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        textAlign: 'center'
      }}>
        {/* Header */}
        <Typography variant="h5" sx={{
          fontWeight: 700,
          mb: 1,
          color: 'text.primary'
        }}>
          {t("orderManagement.invoice.paymentStatus")}
        </Typography>
        
        {/* Payment IDs Display */}
        <Box sx={{ mt: 2, mb: 3, p: 2, background: alpha(COLORS.primary.main, 0.05), borderRadius: 2 }}>
          {subscriptionId ? (
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              mb: 0.5
            }}>
              <strong>Subscription ID:</strong> {subscriptionId}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'error.main', mb: 0.5 }}>
              ⚠️ Subscription ID: Missing
            </Typography>
          )}
          
         
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Verifying your payment...
            </Typography>
          </Box>
        )}

        {/* Success State */}
        {success === true && (
          <Box>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: alpha('#10b981', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircleOutlineIcon sx={{
                fontSize: 48,
                color: '#10b981'
              }} />
            </Box>
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              {resultMessage}
            </Alert>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Your featured listing is now active!
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {success === false && (
          <Box>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: alpha('#ef4444', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <ErrorOutlineIcon sx={{
                fontSize: 48,
                color: '#ef4444'
              }} />
            </Box>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {resultMessage}
            </Alert>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              If you were charged, please contact our support team with your transaction details.
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        {!loading && success !== null && (
          <Box sx={{
            display: 'flex',
            gap: 2,
            mt: 4,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="contained"
              onClick={() => navigate('/layout/makeyourRestaurant')}
              fullWidth
              sx={{
                background: COLORS.primary.main,
                '&:hover': {
                  background: alpha(COLORS.primary.main, 0.9)
                }
              }}
            >
              {success ? 'Back to Featured Listing' : 'Try Again'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/layout/dashboard')}
              fullWidth
              sx={{
                borderColor: COLORS.primary.main,
                color: COLORS.primary.main,
                '&:hover': {
                  borderColor: COLORS.primary.main,
                  background: alpha(COLORS.primary.main, 0.05)
                }
              }}
            >
              Go to Dashboard
            </Button>
          </Box>
        )}

        {/* Debug Info (only shown when params are missing) */}
        {!loading && success === false && (!merchantIdParam || !checkoutId) && (
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left', fontSize: '0.75rem' }}>
            <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 1 }}>
              Debug Information:
            </Typography>
            <Typography variant="caption" component="div">
              • URL Params: {JSON.stringify(Object.fromEntries(searchParams.entries()))}
            </Typography>
            <Typography variant="caption" component="div">
              • merchantId from URL: {searchParams.get('merchantId') || 'Missing'}
            </Typography>
            <Typography variant="caption" component="div">
              • merchantId from localStorage: {localStorage.getItem('hyperpay_merchant_id') || 'Missing'}
            </Typography>
            <Typography variant="caption" component="div">
              • checkoutId from URL (id): {searchParams.get('id') || 'Missing'}
            </Typography>
            <Typography variant="caption" component="div">
              • checkoutId from URL (checkoutId): {searchParams.get('checkoutId') || 'Missing'}
            </Typography>
            <Typography variant="caption" component="div">
              • checkoutId from localStorage: {localStorage.getItem('hyperpay_checkout_id') || 'Missing'}
            </Typography>
            <Typography variant="caption" component="div">
              • Has hash in URL: {urlHasHash ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="caption" component="div">
              • Current URL: {window.location.href}
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentFeaturedHyperPaySuccess;

